import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, MapPin, Briefcase, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { sendEmployeeInvitation } from '@/components/email/sendEmail';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const emptyFormData = {
  first_name: '',
  last_name: '',
  user_email: '',
  employer_id: '',
  phone: '',
  date_of_birth: '',
  gender: 'M',
  ssn_last_four: '',
  tobacco_user: false,
  address: '',
  city: '',
  state: '',
  state_of_residence: '',
  zip_code: '',
  job_title: '',
  department: '',
  hire_date: '',
  employment_status: 'full_time',
  annual_salary: '',
  pay_frequency: 'bi_weekly',
  filing_status: 'single',
  dependents: 0,
  w4_allowances: 0,
  w4_version: 'pre_2020',
  w4_step2_multiple_jobs: false,
  w4_step3_total: 0,
  w4_step4c_extra_withholding: 0,
  w4_claim_exempt: false
};

/**
 * Shared Add/Edit Employee form, used by both EmployeeManagement.jsx (list page)
 * and EmployeeDetail.jsx (detail page's "Edit Employee" button) so the two stay
 * in sync instead of maintaining separate copies of this ~20-field form.
 *
 * `employee` null => Add mode (creates + sends invite). `employee` object => Edit mode.
 */
export default function EmployeeFormDialog({ open, onOpenChange, employee = null, employers = [], onSaved = undefined }) {
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (employee) {
      setFormData({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        user_email: employee.user_email || '',
        employer_id: employee.employer_id || '',
        phone: employee.phone || '',
        date_of_birth: employee.date_of_birth || '',
        gender: employee.gender || 'M',
        ssn_last_four: employee.ssn_last_four || '',
        tobacco_user: employee.tobacco_user || false,
        address: employee.address || '',
        city: employee.city || '',
        state: employee.state || '',
        state_of_residence: employee.state_of_residence || '',
        zip_code: employee.zip_code || '',
        job_title: employee.job_title || '',
        department: employee.department || '',
        hire_date: employee.hire_date || '',
        employment_status: employee.employment_status || 'full_time',
        annual_salary: employee.annual_salary || '',
        pay_frequency: employee.pay_frequency || 'bi_weekly',
        filing_status: employee.filing_status || 'single',
        dependents: employee.dependents || 0,
        w4_allowances: employee.w4_allowances || 0,
        w4_version: employee.w4_version || 'pre_2020',
        w4_step2_multiple_jobs: employee.w4_step2_multiple_jobs || false,
        w4_step3_total: employee.w4_step3_total || 0,
        w4_step4c_extra_withholding: employee.w4_step4c_extra_withholding || 0,
        w4_claim_exempt: employee.w4_claim_exempt || false
      });
    } else {
      setFormData(emptyFormData);
    }
  }, [open, employee]);

  const handleAdd = async () => {
    const existingEmployee = await api.entities.Employee.filter({
      user_email: formData.user_email,
      is_active: true
    });

    if (existingEmployee.length > 0) {
      toast.error('An employee with this email already exists!');
      return;
    }

    const enrollmentToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    await api.entities.Employee.create({
      ...formData,
      enrollment_token: enrollmentToken,
      is_active: true,
      profile_confirmed: false,
      enrollment_status: 'not_started',
      invitation_sent_date: new Date().toISOString()
    });

    const employer = employers.find((e) => e.id === formData.employer_id);
    const inviteInfo = {
      token: enrollmentToken,
      email: formData.user_email,
      name: `${formData.first_name} ${formData.last_name}`,
      url: `${globalThis.location.origin}/InviteCodeEntry`
    };

    try {
      const emailData = sendEmployeeInvitation(formData.user_email, {
        employeeName: inviteInfo.name,
        employerName: employer?.company_name || 'Your Company',
        enrollmentUrl: inviteInfo.url,
        inviteCode: enrollmentToken,
        hrEmail: employer?.hr_contact_email
      });

      await api.functions.invoke('sendSMTPEmail', {
        to: emailData.to,
        subject: emailData.subject,
        html: emailData.html
      });

      toast.success('Employee added and invitation email sent!');
    } catch (error_) {
      console.error('Email sending failed:', error_);
      toast.warning('Employee added! Email failed, please share the invitation code manually.');
    }

    toast.success('Employee added! Share the invitation code with them.');
    onOpenChange(false);
    onSaved?.({ mode: 'create', inviteInfo });
  };

  const handleUpdate = async () => {
    await api.entities.Employee.update(employee.id, formData);

    await api.entities.AuditLog.create({
      entity_type: 'Employee',
      entity_id: employee.id,
      action: 'update',
      performed_by: (await api.auth.me()).email,
      changes: { before: employee, after: formData }
    });

    toast.success('Employee updated successfully');
    onOpenChange(false);
    onSaved?.({ mode: 'update' });
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.user_email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    setSaving(true);
    try {
      if (employee) {
        await handleUpdate();
      } else {
        await handleAdd();
      }
    } catch (err) {
      console.error(err);
      toast.error(employee ? 'Failed to update employee' : 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {employee ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="address">
              <MapPin className="w-4 h-4 mr-2" />
              Address
            </TabsTrigger>
            <TabsTrigger value="employment">
              <Briefcase className="w-4 h-4 mr-2" />
              Employment
            </TabsTrigger>
            <TabsTrigger value="tax">
              <FileText className="w-4 h-4 mr-2" />
              Tax Info
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal" className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.user_email}
                  onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>SSN (Last 4 digits)</Label>
                <Input
                  maxLength={4}
                  value={formData.ssn_last_four}
                  onChange={(e) => setFormData({ ...formData, ssn_last_four: e.target.value })}
                  placeholder="XXXX"
                />
              </div>

              <div className="space-y-2 flex items-center gap-3 pt-6">
                <Checkbox
                  id="tobacco"
                  checked={formData.tobacco_user}
                  onCheckedChange={(checked) => setFormData({ ...formData, tobacco_user: checked })}
                />
                <Label htmlFor="tobacco" className="cursor-pointer">Tobacco User</Label>
              </div>
            </div>
          </TabsContent>

          {/* Address Tab */}
          <TabsContent value="address" className="space-y-4 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(v) => setFormData({ ...formData, state: v, state_of_residence: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment" className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employer *</Label>
                <Select
                  value={formData.employer_id}
                  onValueChange={(v) => setFormData({ ...formData, employer_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employer" />
                  </SelectTrigger>
                  <SelectContent>
                    {employers.map(employer_ => (
                      <SelectItem key={employer_.id} value={employer_.id}>
                        {employer_.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Hire Date</Label>
                <Input
                  type="date"
                  value={formData.hire_date}
                  onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Employment Status</Label>
                <Select
                  value={formData.employment_status}
                  onValueChange={(v) => setFormData({ ...formData, employment_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Pay Frequency</Label>
                <Select
                  value={formData.pay_frequency}
                  onValueChange={(v) => setFormData({ ...formData, pay_frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="semi_monthly">Semi-Monthly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Annual Salary</Label>
                <Input
                  type="number"
                  value={formData.annual_salary}
                  onChange={(e) => setFormData({ ...formData, annual_salary: Number.parseFloat(e.target.value) || '' })}
                  placeholder="85000"
                />
              </div>
            </div>
          </TabsContent>

          {/* Tax Info Tab */}
          <TabsContent value="tax" className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>W-4 Format</Label>
                <Select
                  value={formData.w4_version || 'pre_2020'}
                  onValueChange={(v) => setFormData({ ...formData, w4_version: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post_2020">New (2020+)</SelectItem>
                    <SelectItem value="pre_2020">Old (allowances)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Filing Status</Label>
                <Select
                  value={formData.filing_status}
                  onValueChange={(v) => setFormData({ ...formData, filing_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="head_of_household">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Number of Dependents</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.dependents}
                  onChange={(e) => setFormData({ ...formData, dependents: Number.parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>W-4 Allowances</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.w4_allowances}
                  onChange={(e) => setFormData({ ...formData, w4_allowances: Number.parseInt(e.target.value) || 0 })}
                />
              </div>

              {(formData.w4_version || 'pre_2020') === 'post_2020' && (
                <>
                  <div className="space-y-2">
                    <Label>Step 2 Multiple Jobs</Label>
                    <Select
                      value={formData.w4_step2_multiple_jobs ? 'yes' : 'no'}
                      onValueChange={(v) => setFormData({ ...formData, w4_step2_multiple_jobs: v === 'yes' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Step 3 Total (Annual)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.w4_step3_total || 0}
                      onChange={(e) => setFormData({ ...formData, w4_step3_total: Number.parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Step 4(c) Extra Withholding</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.w4_step4c_extra_withholding || 0}
                      onChange={(e) => setFormData({ ...formData, w4_step4c_extra_withholding: Number.parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Claim Exempt</Label>
                    <Select
                      value={formData.w4_claim_exempt ? 'yes' : 'no'}
                      onValueChange={(v) => setFormData({ ...formData, w4_claim_exempt: v === 'yes' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>State of Residence (for tax)</Label>
                <Select
                  value={formData.state_of_residence || formData.state}
                  onValueChange={(v) => setFormData({ ...formData, state_of_residence: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {employee ? 'Update Employee' : 'Add Employee & Send Invite'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

EmployeeFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  employee: PropTypes.object,
  employers: PropTypes.array,
  onSaved: PropTypes.func
};
